/**
 * lib/sync/DataSyncManager.tsx
 *
 * ESCALA 0B (P1-A / Bloque 8) — Conecta el worker de sincronización de datos al
 * CICLO REAL de sesión. Antes, startDataSyncWorker() existía pero nunca se
 * invocaba. Este gestor:
 *   • arranca el worker SOLO con contexto operativo válido (usuario + tienda);
 *   • lo reinicia al cambiar usuario/empresa/tienda (nuevo scope);
 *   • lo detiene al desmontar o al perder el contexto;
 *   • invalida el estado de sync (sync_runs + sync_meta) al hacer logout / cambiar
 *     de usuario, para que ninguna corrida de otra identidad se reanude.
 *
 * Una sola instancia por scope: la clave (userId:businessId:storeId) evita
 * duplicar workers o timers al re-renderizar. No renderiza UI.
 */
import { useEffect, useRef } from 'react'
import { useAuth } from '@features/auth/hooks/useAuth'
import { clearSyncState, db } from '@lib/dexie/schema'
import {
  startDataSyncWorker,
  pullFreshData,
  reconcileAllData,
  maybeReconcile,
  getServerCatalogRevision,
  __setTestPageSize,
  type SyncContext,
} from './syncWorker'
import { setActiveSyncContext, clearActiveSyncContext } from './syncContext'

export function DataSyncManager(): null {
  const { cajero } = useAuth()
  const userId = cajero?.id ?? null
  const businessId = cajero?.activeBusinessId ?? cajero?.businessId ?? null
  const storeId = cajero?.activeStoreId ?? cajero?.storeId ?? null

  const prevUserRef = useRef<string | null>(null)
  const scopeKey = userId && storeId ? `${userId}:${businessId ?? '_'}:${storeId}` : null

  // Invalidar el estado de sync al cambiar de usuario (incl. logout → userId null).
  useEffect(() => {
    const prev = prevUserRef.current
    if (prev && prev !== userId) {
      void clearSyncState().catch(() => {})
    }
    prevUserRef.current = userId
  }, [userId])

  // Arrancar/parar el worker por scope operativo (una instancia por scope).
  useEffect(() => {
    if (!userId || !storeId) return
    const ctx: SyncContext = { userId, businessId, storeId }
    // 0B.10 P5: instalar el contexto VIGENTE (avanza la generación). Toda corrida en
    // vuelo del scope anterior queda invalidada y no podrá promover.
    setActiveSyncContext(ctx)
    const stop = startDataSyncWorker(ctx)
    return () => {
      stop()
      clearActiveSyncContext()
    }
    // scopeKey resume las tres dimensiones; re-arranca solo si cambia el scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey])

  // Hook de PRUEBA (solo dev/QA — import.meta.env.DEV). NO existe en el bundle
  // productivo (vite build → DEV=false → nunca se asigna). Expone las MISMAS rutas
  // reales que usa la app (reconcileAllData/pullFreshData) para poder disparar una
  // reconciliación/incremental desde Playwright de forma determinista, e inspeccionar
  // sync_runs. No escribe Dexie directamente ni concede privilegios extra.
  useEffect(() => {
    if (!import.meta.env.DEV) return
    if (!userId || !storeId) return
    const ctx: SyncContext = { userId, businessId, storeId }
    const w = window as unknown as { __celmaxxSync?: Record<string, unknown> }
    w.__celmaxxSync = {
      reconcile: () => reconcileAllData(ctx),
      incremental: () => pullFreshData(ctx, 'incremental'),
      // Fase D: decisión event-driven según el change-feed (revisión monotónica).
      maybeReconcile: () => maybeReconcile(ctx, undefined, false),
      getCatalogRevision: () => getServerCatalogRevision(ctx),
      setTestPageSize: (n: number | null) => __setTestPageSize(n),
      getSyncRuns: () => db.sync_runs.toArray(),
    }
    return () => {
      delete w.__celmaxxSync
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey])

  return null
}
